<div class="p-4 sm:p-6 space-y-4 sm:space-y-5">
  <!-- Header -->
  <div class="flex items-start justify-between gap-3">
    <div>
      <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
        {{ 'FEEDBACK.ADMIN.TITLE' | translate }}
      </h1>
      @if (unreadCount() > 0) {
        <p class="text-sm text-emerald-600 dark:text-emerald-400 mt-0.5">
          {{ 'FEEDBACK.ADMIN.NEW_COUNT_ONE' | translate: { count: unreadCount() } }}
        </p>
      } @else {
        <p class="text-sm text-gray-400 mt-0.5">{{ 'FEEDBACK.ADMIN.ALL_READ' | translate }}</p>
      }
    </div>
    <div class="flex items-center gap-2 shrink-0">
      @if (unreadCount() > 0) {
        <button
          (click)="markAllRead()"
          class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
        >
          <lucide-icon [img]="CheckCheck" class="w-3.5 h-3.5"></lucide-icon>
          {{ 'FEEDBACK.ADMIN.MARK_ALL_READ' | translate }}
        </button>
      }
      <button
        (click)="load()"
        class="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <lucide-icon [img]="RefreshCw" class="w-4 h-4"></lucide-icon>
      </button>
    </div>
  </div>

  <!-- Filter tabs -->
  <div class="flex items-center gap-2">
    <button
      (click)="filterUnread.set(false)"
      class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors"
      [class]="
        !filterUnread()
          ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent'
          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
      "
    >
      <lucide-icon [img]="MessageSquare" class="w-3.5 h-3.5"></lucide-icon>
      {{ 'FEEDBACK.ADMIN.ALL_TAB' | translate }}
    </button>
    <button
      (click)="filterUnread.set(true)"
      class="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors"
      [class]="
        filterUnread()
          ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent'
          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
      "
    >
      <span
        class="w-2 h-2 rounded-full bg-emerald-500"
        [class.opacity-0]="unreadCount() === 0"
      ></span>
      {{ 'FEEDBACK.ADMIN.UNREAD_TAB' | translate }}
      @if (unreadCount() > 0) {
        <span
          class="ml-0.5 px-1.5 py-0.5 text-xs bg-emerald-500 text-white rounded-full leading-none"
        >
          {{ unreadCount() }}
        </span>
      }
    </button>
  </div>

  <!-- Content -->
  @if (loading()) {
    <div
      class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center"
    >
      <p class="text-gray-400">{{ 'COMMON.LOADING' | translate }}</p>
    </div>
  } @else if (filtered().length === 0) {
    <div
      class="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-12 text-center"
    >
      <div
        class="w-14 h-14 rounded-2xl bg-gray-50 dark:bg-gray-900 flex items-center justify-center mx-auto mb-3"
      >
        <lucide-icon
          [img]="MessageSquare"
          class="w-7 h-7 text-gray-300 dark:text-gray-600"
        ></lucide-icon>
      </div>
      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">
        {{ (filterUnread() ? 'FEEDBACK.ADMIN.EMPTY_UNREAD' : 'FEEDBACK.ADMIN.EMPTY') | translate }}
      </p>
    </div>
  } @else {
    <div class="space-y-3">
      @for (fb of filtered(); track fb.id) {
        <div
          class="group bg-white dark:bg-gray-800 rounded-2xl border transition-all"
          [class]="
            !fb.isRead
              ? 'border-emerald-200 dark:border-emerald-800 shadow-sm shadow-emerald-50 dark:shadow-emerald-950/20'
              : 'border-gray-100 dark:border-gray-700'
          "
        >
          <div class="p-4 sm:p-5">
            <div class="flex items-start gap-3">
              <!-- Type badge -->
              <div
                class="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center mt-0.5"
                [class]="typeColor(fb.type)"
              >
                <lucide-icon [img]="typeIcon(fb.type)" class="w-4 h-4"></lucide-icon>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <div class="flex items-start justify-between gap-2 flex-wrap">
                  <div class="flex items-center gap-2 flex-wrap">
                    <span
                      class="text-xs font-semibold px-2 py-0.5 rounded-full"
                      [class]="typeColor(fb.type)"
                      >{{ typeLabel(fb.type) }}</span
                    >
                    @if (!fb.isRead) {
                      <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                    }
                  </div>
                  <div class="flex items-center gap-1.5 text-xs text-gray-400 shrink-0">
                    <lucide-icon [img]="Clock" class="w-3 h-3"></lucide-icon>
                    {{ timeAgo(fb.createdAt) }}
                  </div>
                </div>

                <!-- Business info -->
                <div class="mt-1.5 mb-2.5">
                  <span class="text-sm font-semibold text-gray-900 dark:text-white">{{
                    fb.businessName || '—'
                  }}</span>
                  @if (fb.ownerName) {
                    <span class="text-xs text-gray-400 ml-1.5">· {{ fb.ownerName }}</span>
                  }
                </div>

                <!-- Message -->
                <p
                  class="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap"
                >
                  {{ fb.message }}
                </p>

                <!-- Actions -->
                @if (!fb.isRead) {
                  <div class="mt-3 pt-3 border-t border-gray-50 dark:border-gray-700/50">
                    <button
                      (click)="markRead(fb.id)"
                      [disabled]="markingId() === fb.id"
                      class="flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors disabled:opacity-50"
                    >
                      <lucide-icon [img]="CheckCheck" class="w-3.5 h-3.5"></lucide-icon>
                      {{
                        (markingId() === fb.id
                          ? 'FEEDBACK.ADMIN.MARKING'
                          : 'FEEDBACK.ADMIN.MARK_READ'
                        ) | translate
                      }}
                    </button>
                  </div>
                } @else {
                  <div class="mt-2">
                    <span class="text-xs text-gray-300 dark:text-gray-600 flex items-center gap-1">
                      <lucide-icon [img]="CheckCheck" class="w-3 h-3"></lucide-icon>
                      {{ 'FEEDBACK.ADMIN.SEEN' | translate }}
                    </span>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
